<script setup>
import { ref, onMounted, watch } from 'vue'
import ProgressCircle from '../molecules/ProgressCircle.vue'
import QuoteCard from '@/components/atoms/QuoteCard.vue'
import YesNoModal from '../molecules/YesNoModal.vue'
import CheckBoxIcon from '@/assets/icons/checkbox_icon.svg'
import RatingsImage from '@/assets/images/ratings.png'

const emit = defineEmits(['selection'])
const { title, popUpHeader, imgName, quote, items, options, questions } = defineProps([
  'title',
  'popUpHeader',
  'imgName',
  'quote',
  'items',
  'options',
  'questions'
])

const showModal = ref(false)
const currentQuestionIndex = ref(0)
const progress = ref(0)
const popUpAnswers = ref([])
const copiedItems = ref([...items])

function incrementProgressGradually(newProgressValue, totalDuration, onComplete) {
  const startTime = performance.now()
  const startValue = progress.value
  const valueIncrease = newProgressValue - startValue

  function step(timestamp) {
    const elapsed = timestamp - startTime
    const progressRatio = Math.min(elapsed / totalDuration, 1)
    progress.value = Math.floor(startValue + valueIncrease * progressRatio)

    if (progress.value < newProgressValue) {
      requestAnimationFrame(step)
    } else {
      progress.value = newProgressValue
      if (onComplete) onComplete() // Execute callback when animation is done
    }
  }
  requestAnimationFrame(step)
}

onMounted(() => {
  setTimeout(() => {
    incrementProgressGradually(Math.floor(Math.random() * 3) + 13, 700)
  }, 300)
  setTimeout(() => {
    showModal.value = true
  }, 850)
})

watch(progress, (newValue) => {
  if (newValue === 100 && currentQuestionIndex.value === questions.length) {
    showModal.value = false
    emit('selection', popUpAnswers.value)
  }
})

watch(currentQuestionIndex, (newValue) => {
  const firstItem = copiedItems.value.shift();
  copiedItems.value.push(firstItem);
})

watch(showModal, (newValue) => {
  if (newValue) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});

const handleYesNoProgress = (answer) => {
  showModal.value = false
  setTimeout(() => {
    copiedItems.value[0].done = true
    currentQuestionIndex.value++
    let newProgressValue = currentQuestionIndex.value * 25 + (Math.floor(Math.random() * 3) + 13)
    newProgressValue = Math.min(newProgressValue, 100)
    incrementProgressGradually(newProgressValue, 1600, () => {
      if (currentQuestionIndex.value < questions.length) {
        showModal.value = true
      }
    })
    popUpAnswers.value.push(questions[currentQuestionIndex.value - 1].qID + '-' + answer)
  }, 300)
}

</script>

<template>
  <div class="component-container">
    <p class="heading-5">{{ title }}</p>
    <TransitionGroup tag="ul" name="list" class="items" appear>
      <li
        v-for="(item, index) in copiedItems"
        :key="item.title"
        class="item list-item"
      >
        <div class="left-row">
          <p class="left-item-icon">{{ item.icon }}</p>
          <p class="left-item-text">{{ item.title }}</p>
        </div>
        <div v-if="item.done" class="right-row">
          <p class="label-md">Done</p>
          <CheckBoxIcon />
        </div>
        <div v-else class="right-row">
          <div class="label-md">Loading...</div>
        </div>
      </li>
    </TransitionGroup>
    <ProgressCircle :progress="progress" :imgName="imgName" />
    <YesNoModal
      v-if="showModal"
      :questions="questions"
      :options="options"
      :info-text="popUpHeader"
      :current-question-index="currentQuestionIndex"
      @progress="handleYesNoProgress"
    ></YesNoModal>
    <img class="mt-16" :src="RatingsImage" alt="Ratings" />
  </div>
</template>

<style scoped>

.heading-5 {
  text-align: center;
}

.loading-container > * {
  width: 100%;
  max-width: 480px;
}

.items {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 12px;
  gap: 4px;
  padding-left: 20px;
  padding-right: 20px;
}

.item {
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  height: 50px;
}


/* 1. declare transition */
.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

/* 2. declare enter from and leave to state */
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: scaleY(0.01) translate(30px, 0);
}

/* 3. ensure leaving items are taken out of layout flow so that moving
      animations can be calculated correctly. */
.list-leave-active {
  position: absolute;
}

.left-row {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  margin-right: auto;
}

.right-row {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  color: var(--neutrals-300);
}

.right-row > p {
  color: var(--primary-base);
}
</style>
